import React, { useState } from 'react';
import axios from 'axios';

// Define the ABIs and contract addresses for both networks
const mainnetContractAddress = '0x1C6DEF2AF6a7982c08808901049d44E89F8BE026';
const scrollContractAddress = '0xA37526247c20F497AD77CA0ad112B91DC6ec19dC'; 

const MintArticle = ({ articleUrl, title, date, description }) => {
  const [minting, setMinting] = useState(false);
  const [transactionHash, setTransactionHash] = useState('');
  const [isMintLimitReached, setIsMintLimitReached] = useState(false);
  const [mintingStatus, setMintingStatus] = useState(''); 
  const [selectedNetwork, setSelectedNetwork] = useState(
    localStorage.getItem("selectedNetwork") || "mainnet"
  );  

  const handleNetworkChange = (e) => {
    const newNetwork = e.target.value;
    setSelectedNetwork(newNetwork);
    localStorage.setItem("selectedNetwork", newNetwork);
  };

  const MAX_MINTS_PER_ARTICLE = 10;

  const switchNetwork = async () => {
    const networks = {
      'mainnet': {
        chainId: '0x1',
        rpcUrls: ['https://mainnet.infura.io/v3/'],
        chainName: 'Ethereum Mainnet',
        nativeCurrency: {
          name: 'Ethereum',
          symbol: 'ETH',
          decimals: 18,
        },
        blockExplorerUrls: ['https://etherscan.io']
      },
      'scroll': {
        chainId: '0x82750',
        rpcUrls: ['https://rpc.scroll.io/'],
        chainName: 'Scroll',
        nativeCurrency: {
          name: 'Ethereum',
          symbol: 'ETH',
          decimals: 18,
        },
        blockExplorerUrls: ['https://scrollscan.com/']
      },
      // ... add other networks if needed ...
    };

    const network = networks[selectedNetwork];

    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: network.chainId }],
      });
    } catch (error) {
      if (error.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [network],
          });
        } catch (addError) {
          console.error(`Failed to add the ${selectedNetwork} to MetaMask`, addError);
        }
      } else {
        console.error(`Failed to switch to the ${selectedNetwork} in MetaMask`, error);
      }
    }
  };

  const isNetworkError = (error) => {
    // Example checks (you might need to adjust these based on your environment and error types)
    return error.message.includes('Network Error') ||
           error.message.includes('net::ERR') ||
           error.code === 'ECONNREFUSED' ||
           error.code === 'ENOTFOUND' ||
           error.message === 'TypeError: Failed to fetch';
  };

  const retryOperation = async (operation, delay, retries) => {
    try {
      return await operation();
    } catch (error) {
      if (retries > 0 && isNetworkError(error)) {
        console.warn(`Retrying operation (${retries} retries left):`, error);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return retryOperation(operation, delay, retries - 1);
      } else {
        throw error;
      }
    }
  };


  const checkMintLimit = async () => {
    try {
      const Web3 = (await import('web3')).default;
      const ArticleTokenABI = (await import('./ArticleTokenABI.json')).default;

      const web3Instance = new Web3(window.ethereum);

      let contractAddress;
      switch (selectedNetwork) {
        case 'mainnet':
          contractAddress = mainnetContractAddress;
          break;
        case 'scroll':
          contractAddress = scrollContractAddress;
          break;
        default:
          console.error('Unsupported network');
          return;
      }

      const contract = new web3Instance.eth.Contract(ArticleTokenABI, contractAddress);

      const operation = async () => {
        return await contract.methods.getArticleMintCount(articleUrl).call();
      };
      const mintCount = await retryOperation(operation, 1000, 3); 
      setIsMintLimitReached(mintCount >= MAX_MINTS_PER_ARTICLE);
    } catch (error) {
      //console.error('Error checking mint limit:', error);
      setMintingStatus('There was an error checking the mint limit, maybe you need to switch network?');
    }
  };

  const handleMint = async () => {
    if (!window.ethereum) {
      //console.error('MetaMask is not installed');
      setMintingStatus('Please install MetaMask to use this feature!');
      return;
    }

    setMinting(true);

    // Check the mint limit before proceeding
    await checkMintLimit();
    if (isMintLimitReached) {
      setMintingStatus('Mint limit for this article has been reached.');
      setMinting(false);
      return;
    }

    try {

      await switchNetwork();

      const Web3 = (await import('web3')).default;
      const ArticleTokenABI = (await import('./ArticleTokenABI.json')).default;

      const web3Instance = new Web3(window.ethereum);

      let contractAddress;
      switch (selectedNetwork) {
        case 'mainnet':
          contractAddress = mainnetContractAddress;
          break;
        case 'scroll':
          contractAddress = scrollContractAddress;
          break;
      }

      const contract = new web3Instance.eth.Contract(ArticleTokenABI, contractAddress);

      //console.log("Requesting account access...");
      await window.ethereum.request({ method: 'eth_requestAccounts' }); 
      
      const accounts = await web3Instance.eth.getAccounts();
      const userAddress = accounts[0];

      // Define the operation to be retried
      const postToPrepareMint = async () => {
        const response = await axios.post('/api/prepare-mint', {
          articleUrl,
          title,
          date,
          description,
          userAddress,
          network: selectedNetwork
        });
        return response.data.transactionParameters;
      };

      // Retry the operation
      const transactionParameters = await retryOperation(postToPrepareMint, 1000, 3); // Retry 3 times with a 1-second delay

      const txHash = await window.ethereum.request({
        method: 'eth_sendTransaction',
        params: [transactionParameters],
      });

      if (txHash) {
        setTransactionHash(txHash);
        setMintingStatus('Minting sent to the blockchain! Transaction Hash: ' + txHash);
        localStorage.setItem('hasValidNFT', 'true'); // Optimistically set the hasValidNFT flag
      } else {
        //console.error('No transaction hash returned');
      }
    } catch (error) {
      //console.error('Minting failed:', error);
      setMintingStatus('Minting failed: ' + error.message); 
      localStorage.removeItem('hasValidNFT'); // Remove the flag if minting fails
    } finally {
      setMinting(false); 
    }
  };

  return (
    <div className="container mx-auto p-6 border-t-2 border-gray-200 mt-6">
      <div className="text-center">
        <select 
          value={selectedNetwork} 
          onChange={handleNetworkChange} 
          className="bg-white text-black"
        >
          <option value="mainnet">Ethereum Mainnet</option>
          <option value="scroll">Scroll</option>
        </select>
        <button 
          onClick={handleMint} 
          disabled={minting || isMintLimitReached}
          style={{ fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', textShadow: 'rgba(0, 0, 0, 0.12) 0px 1px 0px' }}
          className={`w-full md:w-auto cursor-pointer text-lg font-bold mb-4 px-6 py-3 transition duration-300 ease-in-out mt-4 shadow-md hover:shadow-lg ${minting || isMintLimitReached ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white'} `}
        >
          {isMintLimitReached ? 'Mint Limit Reached' : minting ? 'Minting...' : 'Unlock Timeless Content - Mint Subscription NFT'}
        </button>
        
        <p style={{ fontSize: '16px', fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif' }} className="text-sm mb-2 text-gray-700 dark:text-gray-300">Limited to 10 per Article - Free Subscription For One Year. <a href="#learn-more" className="text-blue-600 underline hover:text-blue-800">Learn more?</a></p>

        {/* Display minting status here */}
        {mintingStatus && (
          <div className={`mt-4 font-medium ${mintingStatus.startsWith('Minting successful') ? 'text-green-600' : 'text-red-600'}`}>
            <p>{mintingStatus}</p>
          </div>
        )}
      </div>
    </div>
  );

};

export default MintArticle;